// ════════════════════════════════════════════════════════════════════
// Glass-i — Proforma "Required Billing JSON Format" — DISPLAY-ONLY template.
//
// Source of truth: docs/analysis/proforma-billing-json-structure-analysis.md
// (section "Recommended Stable Internal Proforma Billing JSON — Superset").
//
// ⚠️ This is a STATIC reference shown read-only in the Proforma JSON viewer so
// developers can compare the current saved PI JSON against the future-safe
// superset. It is NOT a save payload, NOT a mapper, and is NEVER used to change
// existing save/load logic or the API response. Do not import it into any
// calculation, validation, or persistence path.
// ════════════════════════════════════════════════════════════════════

// A representative single line — every documented per-line section is present
// so the shape is self-describing. Values are typed placeholders only.
const TEMPLATE_LINE = Object.freeze({
  identity: { id: '', lineNo: 0, type: '', itemId: '', itemDesc: '', rawItemDesc: '' },
  nesting: {
    parentLineNo: null, nestLevel: 0, narration: '', scrapRate: null,
    legacy: { lineKind: 'SIMPLE', nestType: null }
  },
  item: {
    itemSnapshot: {}, materialTypeCode: '', glassTypeCode: '',
    thicknessCode: '', thicknessMm: null, categoryCode: '', polishTypeCode: null, hsnSac: '', taxability: ''
  },
  dimensions: {
    actualWidthMm: 0, actualHeightMm: 0,
    manualChargeWidthMm: null, manualChargeHeightMm: null,
    chargeWidthMm: 0, chargeHeightMm: 0, qty: 0
  },
  area: { actualSqm: 0, actualSqft: 0, chargeSqm: 0, chargeSqft: 0 },
  pricing: {
    priceUomCode: '', salesRate: 0, alternateRate: 0, basicAmount: 0,
    discPct: 0, lineDiscount: 0, processPricing: [], valueAddedAmount: 0
  },
  lamiDguConfig: { lami: null, dgu: null, sealant: null, desiccant: null },
  fabrication: { details: { entries: [] }, amount: 0 },
  otherCharges: { details: [], amount: 0 },
  remarks: { remarks1: null, remarks2: null, remarks3: null, remarks4: null, remarks5: null },
  tax: { gstRate: 0, cgstAmount: 0, sgstAmount: 0, igstAmount: 0, gstAmount: 0 },
  totals: { taxableAmount: 0, lineTotal: 0 },
  audit: { createdAt: '', updatedAt: '', deletedAt: null }
});

export const REQUIRED_PROFORMA_BILLING_JSON_TEMPLATE = Object.freeze({
  schemaVersion: '1.0',
  document: {
    id: '', proformaNo: '', proformaDate: '', status: '', source: 'PROFORMA',
    customerId: '', createdBy: '', updatedBy: '', createdAt: '', updatedAt: '',
    releasedBy: '', releasedAt: '', deletedAt: null
  },
  controls: {
    wastageMode: '', wastageWidthMm: 0, wastageHeightMm: 0,
    inputUnitCode: '', priceUomCode: '', areaMode: '', editChargeable: false,
    allExistingControlFlags: {
      otherFab: false, showLocation: false, groupByLoc: false, areaVisible: ['SQFT'],
      isSez: false, isExport: false
    }
  },
  customer: {
    customerId: '',
    billTo: {},
    shipTo: {},
    placeOfSupply: { stateCode: '', stateName: '' }
  },
  taxContext: {
    companyStateCode: '', placeOfSupplyStateCode: '',
    supplyType: '', isInterstate: false, isSez: false, isExport: false, multiTaxRates: true
  },
  lines: [TEMPLATE_LINE],
  nestingSummary: {},
  fabricationSummary: {},
  otherChargesSummary: { runtimeOverrides: {}, perRuleTotals: {} },
  totals: {
    totalQty: 0, totalActualSqm: 0, totalActualSqft: 0, totalChargeSqm: 0, totalChargeSqft: 0,
    basicAmount: 0, fabricationAmount: 0, otherChargeAmount: 0, taxableAmount: 0,
    cgstAmount: 0, sgstAmount: 0, igstAmount: 0, gstAmount: 0, roundOffAmount: 0, grandTotal: 0
  },
  events: [],
  futureMappings: {
    taxInvoice: {}, eInvoice: {}, ewayBill: {}, accounting: {}, inventory: {}, production: {}
  },
  rawExistingData: {
    '// purpose': 'Loss-proof carry-forward. Future conversions MUST embed the exact raw '
      + 'GET /api/erp/sales/proformas/:id response here before mapping recognised slots, so '
      + 'unknown/legacy/new columns are never dropped.',
    header: {}, lines: [], events: []
  }
});

// Short carry-forward rules surfaced in the viewer's "Carry Forward Notes" tab.
export const PROFORMA_BILLING_JSON_CARRY_FORWARD_NOTES = Object.freeze([
  'Existing saved PI JSON must not lose any current field during future conversions.',
  'bill_to_snapshot and ship_to_snapshot must be carried forward (verbatim).',
  'item_snapshot must be carried forward for every item-linked line.',
  'Nesting / tree details (type, parentLineNo, nestLevel, narration, scrapRate) must be carried forward.',
  'LAMI / DGU / LAMI+DGU config (nesting_config: lami/dgu/sealant/desiccant) must be carried forward.',
  'fabrication_details ({ entries: [{ code, rate, count }] }) must be carried forward.',
  'other_charge_details and header other_charge_overrides (runtime value overrides) must be carried forward.',
  'remarks1..remarks5 (incl. remark3 / remark4 / remark5) must be carried forward.',
  'Edit-chargeable flag and chargeable dimensions (manual + effective charge W/H) must be carried forward.',
  'Unknown / legacy / future columns must be preserved under rawExistingData — never dropped.',
  'isSez / isExport are not persisted today (reset on reload) — capture at the document before Tax Invoice.',
  'Tax Invoice / E-Invoice / E-Way Bill / Accounting / Inventory / Production implementation is DEFERRED.'
]);

// One-line help per top-level section (shown beside the Required Format template).
export const PROFORMA_BILLING_JSON_SECTION_HELP = Object.freeze({
  document: 'Identity, status, source ("PROFORMA"), and audit/release fields of the document.',
  controls: 'Wastage, units, area mode, edit-chargeable + reserved slots for UI-only flags not persisted today.',
  customer: 'Customer reference plus full bill-to / ship-to snapshots and place of supply.',
  taxContext: 'Seller vs place-of-supply state, derived supply type, interstate / SEZ / export, multi-rate support.',
  lines: 'Per-line: identity, nesting, item, dimensions, area, pricing, LAMI/DGU config, fabrication, other charges, remarks, tax, totals, audit.',
  nestingSummary: 'SHEET cut/scrap/utilization aggregates and combo composition (derived).',
  fabricationSummary: 'Per-fabrication-code totals derived from each line’s entries.',
  otherChargesSummary: 'Runtime value overrides plus per-rule charge totals.',
  totals: 'Every header total column carried verbatim (qty, areas, basic, fab, other-charge, taxable, GST split, round-off, grand total).',
  events: 'Append-only change journal (CREATED / UPDATED / CONFIRMED / HELD / REOPENED / CANCELLED; legacy rows may carry SAVED / RELEASED).',
  futureMappings: 'Reserved projection slots for Tax Invoice / E-Invoice / E-Way Bill / Accounting / Inventory / Production (deferred).',
  rawExistingData: 'Loss-proof carry-forward of the exact raw saved response — unknown fields preserved here.'
});

export default REQUIRED_PROFORMA_BILLING_JSON_TEMPLATE;
